<template>
  <section
    class="support-review-editor"
    aria-label="Destek incelemesi"
  >
    <div class="d-flex align-center mb-3">
      <h3 class="text-subtitle-1">
        İç inceleme
      </h3><v-spacer /><v-btn
        small
        text
        :disabled="saving || dirty"
        :loading="loading"
        @click="load"
      >
        Yenile
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      dense
      outlined
    >
      {{ error }}
    </v-alert>
    <v-alert
      v-if="success"
      type="success"
      dense
      outlined
    >
      {{ success }}
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <template v-if="form">
      <v-row dense>
        <v-col
          cols="12"
          sm="7"
        >
          <v-text-field
            v-model="form.assignee"
            label="Sorumlu"
            maxlength="120"
            outlined
            dense
            :disabled="saving"
          />
        </v-col>
        <v-col
          cols="12"
          sm="5"
        >
          <v-select
            v-model="form.priority"
            :items="priorities"
            label="Öncelik"
            outlined
            dense
            :disabled="saving"
          />
        </v-col>
      </v-row>
      <v-textarea
        v-model="form.internalNote"
        label="İç not"
        counter="4000"
        maxlength="4000"
        rows="3"
        outlined
        :disabled="saving"
      />
      <div
        class="d-flex flex-wrap align-center"
        style="gap:8px"
      >
        <span class="text-caption">{{ saveStatus }}<template v-if="form.updatedBy"> · {{ form.updatedBy }} · {{ formatDate(form.modified) }}</template></span>
        <v-spacer /><v-btn
          text
          small
          :disabled="saving || !dirty"
          @click="reset"
        >
          Vazgeç
        </v-btn>
        <v-btn
          color="primary"
          small
          :disabled="!dirty || loading"
          :loading="saving"
          @click="save"
        >
          İncelemeyi Kaydet
        </v-btn>
      </div>
      <details
        class="mt-4"
        @toggle="loadHistory"
      >
        <summary>Değişiklik geçmişi</summary>
        <v-alert
          v-if="historyError"
          type="error"
          dense
          outlined
        >
          {{ historyError }}
        </v-alert>
        <div
          v-for="revision in history"
          :key="revision.id"
          class="support-review-revision"
        >
          <strong>{{ revision.changedBy }}</strong> · {{ formatDate(revision.created) }}
          <div>{{ revision.fields.assignee || 'Atanmadı' }} · {{ priorityLabel(revision.fields.priority) }}</div>
          <p>{{ revision.fields.internalNote || 'Not yok' }}</p>
        </div>
        <p
          v-if="historyLoaded && !history.length"
          class="text-caption mt-2"
        >
          Henüz değişiklik yok.
        </p>
        <v-btn
          v-if="historyHasMore"
          small
          text
          :loading="historyLoading"
          @click="loadMoreHistory"
        >
          Daha eski kayıtlar
        </v-btn>
      </details>
    </template>
  </section>
</template>
<script>
import axios from 'axios'
import { getAccessToken } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import { formatAdminDate } from '@/utils/adminDate'

export default {
  props: { channel: { type: String, required: true }, recordId: { type: Number, required: true } },
  data: () => ({
    form: null,
    original: null,
    loading: false,
    saving: false,
    error: '',
    success: '',
    sequence: 0,
    history: [],
    historyLoaded: false,
    historyLoading: false,
    historyError: '',
    historyPage: 0,
    historyHasMore: false,
    priorities: [{ text: 'Düşük', value: 'LOW' }, { text: 'Normal', value: 'NORMAL' }, { text: 'Yüksek', value: 'HIGH' }, { text: 'Acil', value: 'URGENT' }],
  }),
  computed: {
    saveStatus() {
      if (this.dirty) return 'Kaydedilmemiş değişiklik'

      return this.form?.version >= 0 ? 'Kaydedildi' : 'İnceleme kaydı yok'
    },
    api() { return `${process.env.VUE_APP_BASE_API_URL}admin/support-reviews/${this.channel}/${this.recordId}` },
    requestOptions() { return { headers: { Authorization: `Bearer ${getAccessToken()}` } } },
    dirty() { return Boolean(this.form && this.original && ['assignee', 'priority', 'internalNote'].some(key => this.form[key] !== this.original[key])) },
  },
  watch: {
    recordId: { immediate: true, handler() { this.load() } },
    dirty(value) { this.$emit('dirty', value) },
    saving(value) { this.$emit('busy', value) },
  },
  beforeDestroy() { this.sequence += 1; this.$emit('dirty', false); this.$emit('busy', false) },
  methods: {
    formatDate: formatAdminDate,
    priorityLabel(value) { return this.priorities.find(row => row.value === value)?.text || value },
    reset() { this.form = { ...this.original }; this.error = ''; this.success = '' },
    async load() {
      this.sequence += 1
      const { sequence } = this
      this.loading = true; this.error = ''; this.success = ''; this.form = null; this.original = null
      this.history = []; this.historyLoaded = false; this.historyHasMore = false; this.historyPage = 0
      this.historyLoading = false; this.historyError = ''
      try {
        const { data } = await axios.get(this.api, this.requestOptions)
        if (sequence !== this.sequence) return
        if (!Number.isInteger(data.version) || data.recordId !== this.recordId) throw new Error('Invalid review')
        this.original = { ...data }; this.form = { ...data }
      } catch (error) { if (sequence === this.sequence) this.error = resolveApiErrorMessage(error, 'İnceleme bilgileri yüklenemedi.') } finally { if (sequence === this.sequence) this.loading = false }
    },
    async save() {
      if (this.saving || this.loading || !this.dirty || this.form.internalNote.length > 4000) return
      this.saving = true; this.error = ''; this.success = ''
      const { sequence } = this
      try {
        const { data } = await axios.put(this.api, {
          version: this.form.version, assignee: this.form.assignee, priority: this.form.priority, internalNote: this.form.internalNote,
        }, this.requestOptions)
        if (sequence !== this.sequence) return
        this.form = { ...data }; this.original = { ...data }; this.success = 'İç inceleme kaydedildi.'
        this.history = []; this.historyLoaded = false; this.historyPage = 0; this.historyHasMore = false
        this.$emit('saved', data)
      } catch (error) { if (sequence === this.sequence) this.error = resolveApiErrorMessage(error, 'İnceleme kaydedilemedi. Değişiklikleriniz korunuyor.') } finally { if (sequence === this.sequence) this.saving = false }
    },
    loadHistory(event) { if (event.target.open && !this.historyLoaded) this.loadMoreHistory() },
    async loadMoreHistory() {
      if (this.historyLoading) return
      this.historyLoading = true; this.historyError = ''
      const { sequence } = this
      try {
        const { data } = await axios.get(`${this.api}/history`, { ...this.requestOptions, params: { page: this.historyPage } })
        if (sequence !== this.sequence) return
        this.history.push(...data.content.map(row => ({ ...row, fields: JSON.parse(row.snapshot) })))
        this.historyPage += 1; this.historyHasMore = this.historyPage < data.totalPages; this.historyLoaded = true
      } catch (error) { if (sequence === this.sequence) this.historyError = resolveApiErrorMessage(error, 'Geçmiş yüklenemedi.') } finally { if (sequence === this.sequence) this.historyLoading = false }
    },
  },
}
</script>
<style scoped>
.support-review-editor { border-top: 1px solid var(--admin-border); padding-top: 16px; }
.support-review-editor h3 { margin: 0; }
.support-review-revision { border-bottom: 1px solid var(--admin-border); padding: 12px 0; font-size: 13px; overflow-wrap: anywhere; }
.support-review-revision p { white-space: pre-wrap; margin-bottom: 0; }
</style>
